<template>
  <div>
    <v-snackbar
      v-for="message in messages.filter((m) => m.show)"
      :key="message.id"
      v-model="message.show"
      style="z-index: 3000"
      :location="location"
      :timeout="message.timeout"
      :color="message.color"
      data-testid="statusBar.snackbar"
      width="60vw"
      @update:model-value="removeOldMessages()"
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3 text-fontStatusBar" size="small" :icon="message.icon"></v-icon>
        <span class="text-fontStatusBar">{{ message.text }}</span>
      </div>
      <template #actions>
        <v-btn
          v-if="message.action"
          variant="text"
          class="text-fontStatusBar"
          @click.stop="message.action.handler(message.id)"
        >
          {{ message.action.label }}
        </v-btn>
        <v-btn
          v-else
          size="x-small"
          icon="mdi-close"
          variant="text"
          class="text-fontStatusBar"
          data-testid="statusBar.snackbar.close"
          @click.stop="message.show = false"
        >
        </v-btn>
      </template>

      <v-progress-linear
        v-if="message.showProgress"
        indeterminate
        height="6"
        absolute
        color="info-progress"
        location="bottom"
      ></v-progress-linear>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from "vue";
import { MessageEventBus } from "../event-bus";
import { Message } from "./message";

withDefaults(
  defineProps<{
    location?:
      | "top"
      | "bottom"
      | "left"
      | "right"
      | "center"
      | "top left"
      | "top right"
      | "bottom left"
      | "bottom right";
  }>(),
  { location: "bottom" }
);

const messages = ref<Message[]>([]);

function removeMessage(id: string) {
  messages.value = messages.value.filter((msg) => msg.id !== id);
}

function removeOldMessages() {
  messages.value = messages.value.filter((msg) => msg.show);
}

function addMessage(message: Message) {
  removeMessage(message.id);
  messages.value.push(message);
}

const errorHandler = (msg: Message) => {
  addMessage({
    id: msg.id,
    color: "error",
    icon: "mdi-cancel",
    text: msg.text,
    timeout: msg.timeout ?? 5000,
    show: true,
    showProgress: msg.showProgress,
    action: msg.action,
  });
};

const warningHandler = (msg: Message) => {
  addMessage({
    id: msg.id,
    color: "warning",
    icon: "mdi-alert",
    text: msg.text,
    timeout: msg.timeout ?? 5000,
    show: true,
    showProgress: msg.showProgress,
    action: msg.action,
  });
};

const successHandler = (msg: Message) => {
  addMessage({
    id: msg.id,
    color: "success",
    icon: "mdi-check-circle",
    text: msg.text,
    timeout: msg.timeout ?? 5000,
    show: true,
    showProgress: msg.showProgress,
    action: msg.action,
  });
};

const informationHandler = (msg: Message) => {
  addMessage({
    id: msg.id,
    color: "information",
    icon: "mdi-information",
    text: msg.text,
    timeout: msg.timeout ?? 5000,
    show: true,
    showProgress: msg.showProgress,
    action: msg.action,
  });
};

onMounted(() => {
  messages.value = [];
  MessageEventBus.on("error", errorHandler);
  MessageEventBus.on("warning", warningHandler);
  MessageEventBus.on("success", successHandler);
  MessageEventBus.on("information", informationHandler);
});

onBeforeUnmount(() => {
  MessageEventBus.off("error", errorHandler);
  MessageEventBus.off("warning", warningHandler);
  MessageEventBus.off("success", successHandler);
  MessageEventBus.off("information", informationHandler);
});
</script>
